import { ListCounter } from '@hotjar/feedback/components/common/ListCounter';
import { ColumnsPicker } from '@hotjar/feedback/components/Surveys/SurveyView/ColumnsPicker';
import { ExportResults } from '@hotjar/feedback/components/common/ExportResults';
import styled from '@emotion/styled';
import { Stack, Text, tokens } from '@hotjar/hotsauce';

import { useTranslation } from '@hotjar/translations';
import type { SurveyColumnOptions } from '../../../domain/Surveys/Responses/SurveyResponsesColumns';
import { QuestionSelector } from '../QuestionSelector';
import type { Survey } from '../../..';
import { ForwardSurveyResults } from './SurveyResultsView/ForwardSurveyResults';
import { useMergedSurveyViewExperiment } from '../hooks/useMergedSurveyViewExperiment';

type ResponseTableControlsProps = {
  pageResponseCount: number;
  survey: Survey;
  columnOptions: SurveyColumnOptions;
  onColumnUpdated: (columns: SurveyColumnOptions) => void;
  onExportSelected: (exportType: 'csv' | 'xlsx') => void;
  onEditForwarding: () => void;
  totalCount?: number;
};

const QuestionSelectorWrapper = styled.div`
  margin: 20px 0;
  max-width: 600px;
  width: 100%;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  grid-gap: ${tokens.spacer.x0pt5};
`;

const ControlsWrapper = styled(Stack)`
  align-items: center;
  justify-content: space-between;
  min-height: ${tokens.spacer.x4};
`;

const CountActionsWrapper = styled(Stack)`
  align-items: center;
`;

export const ResponsesTableControls = ({
  pageResponseCount,
  survey,
  columnOptions,
  onColumnUpdated,
  onExportSelected,
  onEditForwarding,
  totalCount,
}: ResponseTableControlsProps): JSX.Element | null => {
  const { t } = useTranslation('feedback');

  const isInMergedViewExperiment = useMergedSurveyViewExperiment();

  return (
    <ControlsWrapper spacing="x2">
      {isInMergedViewExperiment && (
        <Text as="h2" textType="header5">
          {t('survey-list.table.header.responses')}
        </Text>
      )}
      {!isInMergedViewExperiment && (
        <QuestionSelectorWrapper>
          <QuestionSelector
            surveyId={survey.id}
            trackingEventName="Response list question selected"
            allowAll
          />
        </QuestionSelectorWrapper>
      )}

      <CountActionsWrapper spacing="x2">
        <ListCounter count={pageResponseCount} totalCount={totalCount} />
        <ActionsWrapper>
          <ColumnsPicker columns={columnOptions} onColumnsUpdated={onColumnUpdated} />
          <ExportResults csv xlsx onExportSelected={onExportSelected} />
          <ForwardSurveyResults
            surveyId={survey.id}
            onEdit={onEditForwarding}
            trackingUiPosition="Survey Responses List"
            trackingState="Survey Responses"
          />
        </ActionsWrapper>
      </CountActionsWrapper>
    </ControlsWrapper>
  );
};
